import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import {
  createMarket,
  deleteMarket,
  fetchAssignedZips,
  fetchMarket,
  fetchMarkets,
  updateMarket,
} from 'src/redux/markets/api';
import type {
  AssignedZipEntry,
  BlockedZipInfo,
  Market,
  MarketDetailResponse,
  MarketsState,
} from 'src/redux/markets/types';

// Builds the flat `zip -> { market_id, market_name }` lookup of which market owns
// each zip. When `excludeMarketId` is provided, that market's own entries are
// skipped (used when viewing/editing a market so it doesn't block its own zips).
const buildAssignedZipsLookup = (
  entries: AssignedZipEntry[] | undefined,
  excludeMarketId?: string,
): Record<string, BlockedZipInfo> => {
  const lookup: Record<string, BlockedZipInfo> = {};
  (entries ?? []).forEach((entry) => {
    if (!entry || (excludeMarketId && entry.market_id === excludeMarketId)) return;
    (entry.zip_codes ?? []).forEach((zip) => {
      if (zip) {
        lookup[zip] = { market_id: entry.market_id, market_name: entry.market_name };
      }
    });
  });
  return lookup;
};

const extraReducers = (builder: ActionReducerMapBuilder<MarketsState>) => {
  builder.addCase(fetchMarkets().pending, (state) => {
    state.isLoading = true;
  });

  builder.addCase(fetchMarkets().fulfilled, (state, action) => {
    const isPaginated = Number(action?.meta?.arg?.page || 0) > 1;
    const incomingItems =
      (action?.payload?.data as Market[])?.filter((item) => item?.market_id) || [];

    const incomingIds = incomingItems.map((item) => item.market_id);

    if (isPaginated) {
      state.data = {
        data: [...(state?.data?.data || []), ...incomingIds],
        meta: action?.payload?.meta || state.data.meta,
      };
    } else {
      state.data = {
        data: incomingIds,
        meta: action?.payload?.meta || state.data.meta,
      };
    }

    const newSource = { ...(state?.source || {}) };
    incomingItems.forEach((item) => {
      newSource[item.market_id] = {
        ...(newSource[item.market_id] || {}),
        ...item,
      };
    });
    state.source = newSource;

    if (!isPaginated) {
      const count = (action?.payload?.meta as { unassigned_properties_count?: number })
        ?.unassigned_properties_count;
      state.unassignedPropertiesCount = typeof count === 'number' ? count : 0;
    }

    state.isLoading = false;
  });

  builder.addCase(fetchMarkets().rejected, (state) => {
    state.isLoading = false;
  });

  builder.addCase(createMarket().fulfilled, (state, action) => {
    const response = action.payload as MarketDetailResponse | undefined;
    if (!response?.market_id) return;

    state.source[response.market_id] = {
      market_id: response.market_id,
      market_name: response.market_name ?? '',
      zip_codes: response.zip_codes ?? [],
      zip_code_count: response.zip_codes?.length ?? 0,
      properties_count: response?.properties?.length,
      coordinators: [], //create does not provide coordinators full response, we will populate this field once we navigate to listing page
      coordinator_ids: response.coordinators ?? [],
      market_properties: response.properties ?? [],
    };

    if (!state.data.data.includes(response.market_id)) {
      state.data.data.push(response.market_id);
      state.data.meta.total = (state.data.meta.total ?? 0) + 1;
    }

    state.assignedZips = buildAssignedZipsLookup(response.blocked_zipcodes, response.market_id);
  });

  builder.addCase(updateMarket('').fulfilled, (state, action) => {
    const response = action.payload as MarketDetailResponse | undefined;
    if (!response?.market_id) return;

    const existing = state.source[response.market_id];
    if (!existing) return;

    existing.market_name = response.market_name ?? existing.market_name;
    if (response.zip_codes) {
      existing.zip_codes = response.zip_codes;
      existing.zip_code_count = response.zip_codes.length;
    }
    if (response.coordinators) {
      existing.coordinator_ids = response.coordinators;
    }

    state.assignedZips = buildAssignedZipsLookup(response.blocked_zipcodes, response.market_id);
  });

  builder.addCase(fetchAssignedZips().pending, (state) => {
    state.isAssignedZipsLoading = true;
  });

  builder.addCase(fetchAssignedZips().fulfilled, (state, action) => {
    state.assignedZips = buildAssignedZipsLookup(
      action?.payload as AssignedZipEntry[] | undefined,
    );
    state.isAssignedZipsLoading = false;
  });

  builder.addCase(fetchAssignedZips().rejected, (state) => {
    state.isAssignedZipsLoading = false;
  });

  builder.addCase(fetchMarket('').pending, (state) => {
    state.marketDetailLoading = true;
  });

  builder.addCase(fetchMarket('').fulfilled, (state, action) => {
    const response = action.payload as MarketDetailResponse | undefined;
    if (!response?.market_id) {
      state.marketDetailLoading = false;
      return;
    }

    state.source[response.market_id] = {
      ...(state.source[response.market_id] || {}),
      market_id: response.market_id,
      market_name: response.market_name,
      zip_codes: response.zip_codes ?? [],
      properties_count: state.source[response.market_id]?.properties_count ?? 0,
      coordinators: state.source[response.market_id]?.coordinators ?? [],
      coordinator_ids: response.coordinators ?? [],
      market_properties: response.properties ?? [],
    };

    state.assignedZips = buildAssignedZipsLookup(response.blocked_zipcodes, response.market_id);

    state.marketDetailLoading = false;
  });

  builder.addCase(fetchMarket('').rejected, (state) => {
    state.marketDetailLoading = false;
  });

  builder.addCase(deleteMarket('').pending, (state) => {
    state.isMarketDeleting = true;
  });

  builder.addCase(deleteMarket('').fulfilled, (state, action) => {
    state.isMarketDeleting = false;
    const market_id = (action.payload as { market_id?: string } | undefined)?.market_id;
    if (market_id) {
      delete state.source[market_id];
      state.data.data = state.data.data.filter((id) => id !== market_id);
      state.data.meta.total = Math.max(0, (state.data.meta.total ?? 0) - 1);
    }
  });

  builder.addCase(deleteMarket('').rejected, (state) => {
    state.isMarketDeleting = false;
  });
};

export default extraReducers;
